export const ASK_ORION_ACTIONS = {
  SEND_MESSAGE_REQUEST: "ASK_ORION/SEND_MESSAGE_REQUEST",
  SEND_MESSAGE_SUCCESS: "ASK_ORION/SEND_MESSAGE_SUCCESS",
  SEND_MESSAGE_FAILURE: "ASK_ORION/SEND_MESSAGE_FAILURE",
  ADD_USER_MESSAGE: "ASK_ORION/ADD_USER_MESSAGE",
  RESET_CHAT: "ASK_ORION/RESET_CHAT",
  SET_SESSION_ID: "ASK_ORION/SET_SESSION_ID",
  REVIEW_DOCUMENT_REQUEST: "ASK_ORION/REVIEW_DOCUMENT_REQUEST",
  REVIEW_DOCUMENT_SUCCESS: "ASK_ORION/REVIEW_DOCUMENT_SUCCESS",
  REVIEW_DOCUMENT_FAILURE: "ASK_ORION/REVIEW_DOCUMENT_FAILURE",
  SET_GOOGLE_DOC_META: "ASK_ORION/SET_GOOGLE_DOC_META",
  RESET_REVIEW_STATE: "ASK_ORION/RESET_REVIEW_STATE",
};

interface Message {
  id: string;
  text: string;
  sender: "user" | "assistant";
  timestamp: Date;
  rawResponse?: any;
  options?: Array<{ label: string; value: string }>;
}

interface AskOrionSliceProps {
  sessionId: string | null;
  messages: Message[];
  loading: boolean;
  error: string | null;
  isJsonResponseReceived: boolean;
  finalJsonData: any | null;
  reviewLoading: boolean;
  reviewError: string | null;
  reviewResults: any[];
  documentName: string | null;
  docId: string | null;
  totalIssuesFound: number;
  reviewedFileDriveUrl: string | null;
  reviewedFileId: string | null;
  reviewSummary: any[];
  reviewComments: any;
  googleDocMeta: any | null;
}

const initialState: AskOrionSliceProps = {
  sessionId: null,
  messages: [],
  loading: false,
  error: null,
  isJsonResponseReceived: false,
  finalJsonData: null,
  reviewLoading: false,
  reviewError: null,
  reviewResults: [],
  documentName: null,
  docId: null,
  totalIssuesFound: 0,
  reviewedFileDriveUrl: null,
  reviewedFileId: null,
  reviewSummary: [],
  reviewComments: {},
  googleDocMeta: null,
};

export default function askOrionReducer(state = initialState, action: any) {
  switch (action.type) {
    case ASK_ORION_ACTIONS.SEND_MESSAGE_REQUEST: {
      return {
        ...state,
        loading: true,
        error: null,
      };
    }

    case ASK_ORION_ACTIONS.SEND_MESSAGE_SUCCESS: {
      const { session_id, answer } = action.payload;
      
      // Check if answer contains JSON
      let isJson = false;
      let jsonData = null;
      
      try {
        // Check if the answer contains JSON (wrapped in ```json or just JSON object)
        const jsonMatch = answer.match(/```json\n([\s\S]*?)\n```/) || answer.match(/(\{[\s\S]*?\})/);
        if (jsonMatch) {
          jsonData = JSON.parse(jsonMatch[1]);
          isJson = true;
        }
      } catch (error) {
        // Not a JSON response, continue with regular flow
        console.log("Response is not JSON format");
      }

      const newMessage: Message = {
        id: `assistant-${Date.now()}`,
        text: answer,
        sender: "assistant",
        timestamp: new Date(),
        rawResponse: action.payload,
      };

      return {
        ...state,
        sessionId: session_id || state.sessionId,
        messages: [...state.messages, newMessage],
        loading: false,
        isJsonResponseReceived: isJson,
        finalJsonData: isJson ? jsonData : state.finalJsonData,
      };
    }

    case ASK_ORION_ACTIONS.SEND_MESSAGE_FAILURE: {
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    }

    case ASK_ORION_ACTIONS.ADD_USER_MESSAGE: {
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        text: action.payload,
        sender: "user",
        timestamp: new Date(),
      };

      return {
        ...state,
        messages: [...state.messages, userMessage],
      };
    }

    case ASK_ORION_ACTIONS.RESET_CHAT: {
      return {
        ...initialState,
      };
    }

    case ASK_ORION_ACTIONS.SET_SESSION_ID: {
      return {
        ...state,
        sessionId: action.payload,
      };
    }

    case ASK_ORION_ACTIONS.REVIEW_DOCUMENT_REQUEST: {
      return {
        ...state,
        reviewLoading: true,
        reviewError: null,
      };
    }

    case ASK_ORION_ACTIONS.REVIEW_DOCUMENT_SUCCESS: {
      const data = action.payload.data || action.payload;
      
      // Flatten review_comments object to array format
      let reviewResults = [];
      if (data.review_comments && typeof data.review_comments === 'object') {
        reviewResults = Object.keys(data.review_comments).flatMap(category => 
          data.review_comments[category].map((comment: any) => ({
            ...comment,
            category: category
          }))
        );
      } else if (Array.isArray(data.review_results)) {
        reviewResults = data.review_results;
      }
      
      // Deep clone reviewComments to prevent state mutation
      let reviewComments = {};
      if (data.review_comments && typeof data.review_comments === 'object') {
        reviewComments = Object.keys(data.review_comments).reduce((acc, category) => {
          acc[category] = data.review_comments[category].map((comment: any) => ({ ...comment }));
          return acc;
        }, {} as any);
      }
      
      // Deep clone reviewSummary to prevent state mutation
      const reviewSummary = Array.isArray(data.review_summary) 
        ? data.review_summary.map((item: any) => ({ ...item }))
        : [];
      
      return {
        ...state,
        reviewLoading: false,
        reviewResults: reviewResults,
        documentName: data.document_name || data.doc_details?.display_name || null,
        docId: data.doc_id || data.doc_details?.id || null,
        totalIssuesFound: data.total_issues_found || 0,
        reviewedFileDriveUrl: data.reviewed_file_drive_url || data.doc_details?.drive_details?.reviewed_docx_access_url || null,
        reviewedFileId: data.reviewed_docx_file_id || data.doc_details?.drive_details?.reviewed_file_id || data.drive_details?.reviewed_file_id || null,
        reviewSummary: reviewSummary,
        reviewComments: reviewComments,
      };
    }

    case ASK_ORION_ACTIONS.REVIEW_DOCUMENT_FAILURE: {
      return {
        ...state,
        reviewLoading: false,
        reviewError: action.payload,
      };
    }

    case ASK_ORION_ACTIONS.SET_GOOGLE_DOC_META: {
      return {
        ...state,
        googleDocMeta: action.payload,
      };
    }

    case ASK_ORION_ACTIONS.RESET_REVIEW_STATE: {
      return {
        ...state,
        reviewLoading: false,
        reviewError: null,
        reviewResults: [],
        documentName: null,
        docId: null,
        totalIssuesFound: 0,
        reviewedFileDriveUrl: null,
        reviewedFileId: null,
        reviewSummary: [],
        reviewComments: {},
        googleDocMeta: null,
      };
    }

    default:
      return state;
  }
}
